import {
    HttpErrorResponse,
    HttpEvent,
    HttpInterceptorFn,
    HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { ApmService } from '@elastic/apm-rum-angular';
import { catchError, finalize, tap, throwError } from 'rxjs';

const REDACTED_VALUE = '[REDACTED]';
const SENSITIVE_FIELD_PATTERN = /pass(word)?|token|secret|authorization|api[-_]?key/i;

/**
 * Logs every outgoing HTTP request as a dedicated Elastic APM transaction.
 *
 * Notes:
 * - Uses `ApmService.apm` because the Angular wrapper exposes the underlying RUM agent there.
 * - Redacts sensitive query-string values before adding labels.
 * - Records only payload size, never the raw request body.
 */
export const apmHttpInterceptor: HttpInterceptorFn = (req, next) => {
    const apmService = inject(ApmService);
    const apm = apmService.apm;

    const requestStartedAt = getNow();
    const sanitizedUrl = sanitizeUrl(req.urlWithParams);
    const requestPayloadBytes = getPayloadSize(req.body);
    const transaction = apm.startTransaction(
        buildTransactionName(req.method, sanitizedUrl),
        'http-request'
    );

    transaction?.addLabels({
        api_url: sanitizedUrl,
        http_method: req.method,
        request_payload_bytes: requestPayloadBytes,
    });

    transaction?.addContext({
        http: {
            method: req.method,
            url: sanitizedUrl,
        },
    });

    return next(req).pipe(
        tap({
            next: (event: HttpEvent<unknown>) => {
                if (!(event instanceof HttpResponse)) {
                    return;
                }

                transaction?.addLabels({
                    http_status_code: event.status,
                    response_time_ms: roundDuration(getNow() - requestStartedAt),
                    request_outcome: 'success',
                });
            },
        }),
        catchError((error: unknown) => {
            const responseTimeMs = roundDuration(getNow() - requestStartedAt);
            const statusCode = error instanceof HttpErrorResponse ? error.status : 0;

            transaction?.addLabels({
                http_status_code: statusCode,
                response_time_ms: responseTimeMs,
                request_outcome: 'failure',
            });

            transaction?.addContext({
                error: buildErrorContext(error),
            });

            apm.captureError(toApmError(error), {
                labels: {
                    api_url: sanitizedUrl,
                    http_method: req.method,
                    request_payload_bytes: requestPayloadBytes,
                    response_time_ms: responseTimeMs,
                },
            });

            return throwError(() => error);
        }),
        finalize(() => {
            transaction?.end();
        })
    );
};

function buildTransactionName(method: string, sanitizedUrl: string): string {
    return `${method.toUpperCase()} ${sanitizedUrl}`;
}

function sanitizeUrl(url: string): string {
    try {
        const baseOrigin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
        const parsedUrl = new URL(url, baseOrigin);

        for (const key of Array.from(parsedUrl.searchParams.keys())) {
            if (SENSITIVE_FIELD_PATTERN.test(key)) {
                parsedUrl.searchParams.set(key, REDACTED_VALUE);
            }
        }

        const basePath = parsedUrl.origin === baseOrigin
            ? parsedUrl.pathname
            : `${parsedUrl.origin}${parsedUrl.pathname}`;
        const queryString = parsedUrl.searchParams.toString();

        return queryString ? `${basePath}?${queryString}` : basePath;
    } catch {
        return url.replace(/([?&])([^=]+)=([^&]*)/gi, (segment, separator, key) => {
            return SENSITIVE_FIELD_PATTERN.test(String(key))
                ? `${separator}${key}=${REDACTED_VALUE}`
                : segment;
        });
    }
}

function getPayloadSize(body: unknown): number {
    if (body == null) {
        return 0;
    }

    if (typeof body === 'string') {
        return encodeUtf8(body).length;
    }

    if (body instanceof Blob) {
        return body.size;
    }

    if (body instanceof ArrayBuffer) {
        return body.byteLength;
    }

    if (ArrayBuffer.isView(body)) {
        return body.byteLength;
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return encodeUtf8(body.toString()).length;
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return estimateFormDataSize(body);
    }

    try {
        return encodeUtf8(JSON.stringify(redactSensitiveFields(body))).length;
    } catch {
        return 0;
    }
}

function estimateFormDataSize(formData: FormData): number {
    let totalBytes = 0;

    formData.forEach((value, key) => {
        totalBytes += encodeUtf8(key).length;

        if (value instanceof Blob) {
            totalBytes += value.size;
            return;
        }

        totalBytes += encodeUtf8(
            SENSITIVE_FIELD_PATTERN.test(key) ? REDACTED_VALUE : String(value)
        ).length;
    });

    return totalBytes;
}

function redactSensitiveFields(value: unknown, seen = new WeakSet<object>()): unknown {
    if (value == null || typeof value !== 'object') {
        return value;
    }

    if (value instanceof Date || value instanceof Blob || value instanceof ArrayBuffer) {
        return value;
    }

    if (seen.has(value)) {
        return '[Circular]';
    }

    seen.add(value);

    if (Array.isArray(value)) {
        return value.map((item) => redactSensitiveFields(item, seen));
    }

    const sanitizedRecord: Record<string, unknown> = {};

    for (const [key, nestedValue] of Object.entries(value)) {
        sanitizedRecord[key] = SENSITIVE_FIELD_PATTERN.test(key)
            ? REDACTED_VALUE
            : redactSensitiveFields(nestedValue, seen);
    }

    return sanitizedRecord;
}

function buildErrorContext(error: unknown): Record<string, string | number> {
    if (error instanceof HttpErrorResponse) {
        return {
            name: error.name,
            message: error.message,
            status: error.status,
            statusText: error.statusText,
            url: sanitizeUrl(error.url ?? ''),
        };
    }

    if (error instanceof Error) {
        return {
            name: error.name,
            message: error.message,
        };
    }

    return {
        message: typeof error === 'string' ? error : 'Unknown HTTP error',
    };
}

function toApmError(error: unknown): Error | string {
    if (error instanceof Error) {
        return error;
    }

    if (error instanceof HttpErrorResponse) {
        const apmError = new Error(error.message);
        apmError.name = error.name;
        return apmError;
    }

    return typeof error === 'string' ? error : 'Unknown HTTP error';
}

function roundDuration(durationMs: number): number {
    return Math.round(durationMs * 100) / 100;
}

function getNow(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function encodeUtf8(value: string): Uint8Array {
    return new TextEncoder().encode(value);
}